import React, { useState, useEffect } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { ref, push, set, get } from 'firebase/database';
import { auth, database } from '../../firebase/config';
import { trackSignup, trackLogin } from '../../firebase-analytics';

// Generate unique invite code
const generateInviteCode = () => {
  const adjectives = ['ROCK', 'JAZZ', 'FUNK', 'SOUL', 'BLUE', 'PUNK'];
  const randomAdj = adjectives[Math.floor(Math.random() * adjectives.length)];
  const randomNum = Math.floor(1000 + Math.random() * 9000);
  return `${randomAdj}-${randomNum}`;
};

export default function AuthScreen() {
  // Detect browser language and set default
  const detectLanguage = () => {
    const saved = localStorage.getItem('tapsody_language');
    if (saved) return saved;
    
    const browserLang = navigator.language || navigator.userLanguage;
    return browserLang.startsWith('nl') ? 'nl' : 'en';
  };

  const [authMode, setAuthMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [bandName, setBandName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [showInviteCode, setShowInviteCode] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [language, setLanguage] = useState(detectLanguage());
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Language content for auth form
  const content = {
    nl: {
      welcome: "Welkom bij Tapsody",
      subtitle: "Start gratis - geen creditcard nodig",
      login: "Inloggen",
      signup: "Aanmelden",
      email: "E-mail",
      emailPlaceholder: "jouw e-mail",
      password: "Wachtwoord",
      passwordPlaceholder: "minimaal 6 tekens",
      bandName: "Bandnaam",
      bandNamePlaceholder: "Hoe heet je band?",
      bandNameHelp: "Je kunt dit later nog aanpassen",
      inviteCode: "Uitnodigingscode",
      inviteCodePlaceholder: "bijv. ROCK-4829",
      joinExisting: "Sluit aan bij bestaande band",
      hideInvite: "Toch nieuwe band maken",
      reassurance: "100% gratis proberen • Geen verplichtingen",
      createAccount: "Account Aanmaken",
      loggingIn: "Inloggen...",
      creatingAccount: "Account aanmaken...",
      or: "of",
      errors: {
        fillEmailPassword: "Vul e-mail en wachtwoord in",
        fillBandOrInvite: "Vul een bandnaam in of gebruik een uitnodigingscode",
        passwordTooShort: "Wachtwoord moet minimaal 6 tekens zijn",
        emailInUse: "Dit e-mailadres is al geregistreerd. Log in of gebruik een ander e-mailadres.",
        invalidCredentials: "Onjuist e-mailadres of wachtwoord",
        invalidInviteCode: "Ongeldige uitnodigingscode. Controleer de code en probeer opnieuw.",
        fillEmail: "Vul je e-mailadres in",
        userNotFound: "Geen account gevonden met dit e-mailadres"
      },
      forgotPassword: "Wachtwoord vergeten?",
      resetPassword: "Wachtwoord resetten",
      resetInstructions: "Vul je e-mailadres in en we sturen je een link om je wachtwoord te resetten.",
      sendResetLink: "Verstuur reset link",
      sendingResetLink: "Link versturen...",
      resetSuccess: "We hebben je een e-mail gestuurd met instructies om je wachtwoord te resetten.",
      backToLogin: "Terug naar inloggen"
    },
    en: {
      welcome: "Welcome to Tapsody",
      subtitle: "Start free - no credit card required",
      login: "Log In",
      signup: "Sign Up",
      email: "Email",
      emailPlaceholder: "your email",
      password: "Password",
      passwordPlaceholder: "minimum 6 characters",
      bandName: "Band Name",
      bandNamePlaceholder: "What's your band called?",
      bandNameHelp: "You can change this later",
      inviteCode: "Invite Code",
      inviteCodePlaceholder: "e.g., ROCK-4829",
      joinExisting: "Join existing band",
      hideInvite: "Create new band instead",
      reassurance: "100% free trial • No commitments",
      createAccount: "Create Account",
      loggingIn: "Logging in...",
      creatingAccount: "Creating account...",
      or: "or",
      errors: {
        fillEmailPassword: "Please fill in email and password",
        fillBandOrInvite: "Please enter a band name or use an invite code",
        passwordTooShort: "Password must be at least 6 characters",
        emailInUse: "This email is already registered. Please login or use a different email.",
        invalidCredentials: "Invalid email or password",
        invalidInviteCode: "Invalid invite code. Please check the code and try again.",
        fillEmail: "Please enter your email address",
        userNotFound: "No account found with this email address"
      },
      forgotPassword: "Forgot password?",
      resetPassword: "Reset Password",
      resetInstructions: "Enter your email address and we'll send you a link to reset your password.",
      sendResetLink: "Send Reset Link",
      sendingResetLink: "Sending link...",
      resetSuccess: "We've sent you an email with instructions to reset your password.",
      backToLogin: "Back to login"
    }
  };

  const t = content[language];

  const toggleLanguage = () => {
    const newLang = language === 'nl' ? 'en' : 'nl';
    setLanguage(newLang);
    localStorage.setItem('tapsody_language', newLang);
  };

  // Catch any uncaught errors
  useEffect(() => {
    const errorHandler = (event) => {
      console.error('UNCAUGHT ERROR:', event.error);
      console.error('Error message:', event.message);
      console.error('Stack:', event.error?.stack);
    };
    window.addEventListener('error', errorHandler);
    return () => window.removeEventListener('error', errorHandler);
  }, []);

  const handleSignup = async (e) => {
    e.preventDefault();
    
    console.log('🎯 Signup attempt started');
    
    // Validate required fields
    if (!email || !password) {
      setAuthError(t.errors.fillEmailPassword);
      console.log('❌ Validation failed: missing email/password');
      return;
    }
    
    // Band name only required if NOT joining with invite code
    if (!inviteCode.trim() && !bandName.trim()) {
      setAuthError(t.errors.fillBandOrInvite);
      console.log('❌ Validation failed: missing band name');
      return;
    }
    
    if (password.length < 6) {
      setAuthError(t.errors.passwordTooShort);
      console.log('❌ Validation failed: password too short');
      return;
    }

    try {
      setAuthLoading(true);
      setAuthError('');

      // Check if joining existing band with invite code
      if (inviteCode.trim()) {
        console.log('🎯 Flow: Joining existing band with invite code');
        
        // Create user account FIRST
        let userCredential;
        let displayName;
        
        try {
          userCredential = await createUserWithEmailAndPassword(auth, email, password);
          displayName = email.split('@')[0];
          await updateProfile(userCredential.user, { displayName });
          console.log('✅ User account created');
        } catch (authError) {
          if (authError.code === 'auth/email-already-in-use') {
            setAuthError(t.errors.emailInUse);
          } else {
            setAuthError(`${language === 'nl' ? 'Account maken mislukt' : 'Account creation failed'}: ${authError.message}`);
          }
          console.log('❌ Account creation failed:', authError.code);
          setAuthLoading(false);
          return;
        }
        
        // Search for invite code
        let foundBandId = null;
        
        try {
          const bandsRef = ref(database, 'bands');
          const snapshot = await get(bandsRef);
          
          if (snapshot.exists()) {
            const bands = snapshot.val();
            Object.keys(bands).forEach(id => {
              if (bands[id].inviteCode === inviteCode.trim().toUpperCase()) {
                foundBandId = id;
              }
            });
          }
        } catch (readError) {
          try {
            await userCredential.user.delete();
          } catch (deleteError) {
            console.error('Failed to delete user:', deleteError);
          }
          
          setAuthError(`${language === 'nl' ? 'Kan uitnodigingscode niet vinden' : 'Cannot search for invite code'}: ${readError.message}`);
          console.log('❌ Invite code search failed');
          setAuthLoading(false);
          return;
        }

        if (!foundBandId) {
          try {
            await userCredential.user.delete();
          } catch (deleteError) {
            console.error('Failed to delete user:', deleteError);
          }
          
          setAuthError(t.errors.invalidInviteCode);
          console.log('❌ Invalid invite code');
          setAuthLoading(false);
          return;
        }

        // Write to database
        try {
          await set(ref(database, `bandMembers/${userCredential.user.uid}`), { 
            bandId: foundBandId 
          });

          await set(ref(database, `bands/${foundBandId}/members/${userCredential.user.uid}`), {
            email: email,
            displayName: displayName,
            joinedAt: new Date().toISOString(),
            role: 'member'
          });
          
          console.log('✅ Successfully joined existing band');
          trackSignup();
        } catch (dbError) {
          try {
            await userCredential.user.delete();
          } catch (deleteError) {
            console.error('Failed to delete user:', deleteError);
          }
          
          setAuthError(`${language === 'nl' ? 'Kan niet aansluiten bij band' : 'Unable to join band'}: ${dbError.message}`);
          console.log('❌ Database write failed');
          setAuthLoading(false);
          return;
        }
      } else {
        // Create new band
        console.log('🎯 Flow: Creating new band');
        
        let userCredential;
        let displayName;
        
        try {
          userCredential = await createUserWithEmailAndPassword(auth, email, password);
          displayName = email.split('@')[0];
          await updateProfile(userCredential.user, { displayName });
          console.log('✅ User account created');
        } catch (authError) {
          if (authError.code === 'auth/email-already-in-use') {
            setAuthError(t.errors.emailInUse);
          } else {
            setAuthError(`${language === 'nl' ? 'Account maken mislukt' : 'Account creation failed'}: ${authError.message}`);
          }
          console.log('❌ Account creation failed:', authError.code);
          setAuthLoading(false);
          return;
        }

        // Create new band
        const newBandId = push(ref(database, 'bands')).key;
        const newInviteCode = generateInviteCode();

        await set(ref(database, `bandMembers/${userCredential.user.uid}`), {
          bandId: newBandId
        });

        await set(ref(database, `bands/${newBandId}`), {
          name: bandName,
          inviteCode: newInviteCode,
          createdAt: new Date().toISOString(),
          createdBy: userCredential.user.uid,
          members: {
            [userCredential.user.uid]: {
              email: email,
              displayName: displayName,
              joinedAt: new Date().toISOString(),
              role: 'leader'
            }
          }
        });
        
        console.log('✅ Successfully created new band');
        trackSignup();
      }

      setEmail('');
      setPassword('');
      setBandName('');
      setInviteCode('');
    } catch (error) {
      console.error('❌ Signup error:', error);
      setAuthError(error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    console.log('🎯 Login attempt started');
    
    if (!email || !password) {
      setAuthError(t.errors.fillEmailPassword);
      return;
    }

    try {
      setAuthLoading(true);
      setAuthError('');
      await signInWithEmailAndPassword(auth, email, password);
      setEmail('');
      setPassword('');
      console.log('✅ Login successful');
      trackLogin();
    } catch (error) {
      setAuthError(t.errors.invalidCredentials);
      console.log('❌ Login failed:', error.code);
    } finally {
      setAuthLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    console.log('🎯 Password reset attempt started');
    
    if (!email) {
      setAuthError(t.errors.fillEmail);
      return;
    }

    try {
      setAuthLoading(true);
      setAuthError('');
      await sendPasswordResetEmail(auth, email);
      setResetSuccess(true);
      console.log('✅ Password reset email sent');
    } catch (error) {
      console.log('❌ Password reset failed:', error.code);
      if (error.code === 'auth/user-not-found') {
        setAuthError(t.errors.userNotFound);
      } else {
        setAuthError(error.message);
      }
    } finally {
      setAuthLoading(false);
    }
  };

  // Auth form
  return (
    <div className="min-h-screen app-bg text-white flex items-center justify-center p-4">
      <div className="app-bg-orbs">
        <div className="app-bg-orb app-bg-orb-aqua" />
        <div className="app-bg-orb app-bg-orb-magenta" />
      </div>
      <div className="w-full max-w-md">
        <div className="flex items-center justify-end mb-4">
          <button
            onClick={toggleLanguage}
            className="text-text-subtle hover:text-white transition text-sm font-semibold"
          >
            {language === 'nl' ? 'EN' : 'NL'}
          </button>
        </div>

        <div className="text-center mb-8">
          <img src={`${process.env.PUBLIC_URL}/tapsody-logo.png`} alt="Tapsody Logo" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-2">{t.welcome}</h1>
          <p className="text-text-subtle">{t.subtitle}</p>
        </div>

        <div className="card-3d rounded-card p-6">
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => { 
                setAuthMode('login'); 
                setAuthError(''); 
                console.log('🎯 Switched to Login tab');
              }}
              className={`flex-1 py-2 font-semibold transition rounded-card-btn ${authMode === 'login' ? 'bg-brand text-white border border-brand' : 'bg-transparent text-white border border-surface-border-light'}`}
              
            >
              {t.login}
            </button>
            <button
              onClick={() => { 
                setAuthMode('signup'); 
                setAuthError(''); 
                console.log('🎯 Switched to Signup tab');
              }}
              className={`flex-1 py-2 font-semibold transition rounded-card-btn ${authMode === 'signup' ? 'bg-brand text-white border border-brand' : 'bg-transparent text-white border border-surface-border-light'}`}
              
            >
              {t.signup}
            </button>
          </div>

          {authError && (
            <div className="bg-red-900/50 rounded-card p-3 mb-4">
              <p className="text-red-100 text-sm">{authError}</p>
            </div>
          )}

          <form onSubmit={authMode === 'login' ? handleLogin : handleSignup} className="space-y-4">
            {/* EMAIL */}
            <div>
              <label className="block text-sm font-semibold mb-2">{t.email}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.emailPlaceholder}
                className="w-full bg-transparent border border-surface-border-light px-4 py-3 text-white placeholder-text-subtle focus:outline-none focus:border-brand rounded-pill"
                
              />
            </div>

            {/* PASSWORD */}
            <div>
              <label className="block text-sm font-semibold mb-2">{t.password}</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.passwordPlaceholder}
                  className="w-full bg-transparent border border-surface-border-light px-4 py-3 pr-12 text-white placeholder-text-subtle focus:outline-none focus:border-brand rounded-pill"
                  
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-text-subtle hover:text-white transition"
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {authMode === 'login' && (
                <div className="text-right mt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setShowResetPassword(true);
                      setAuthError('');
                      setResetSuccess(false);
                      console.log('🎯 Showing password reset');
                    }}
                    className="text-sm text-brand hover:text-brand-magenta underline"
                  >
                    {t.forgotPassword}
                  </button>
                </div>
              )}
            </div>

            {/* BAND NAME - Only for signup, when not using invite code */}
            {authMode === 'signup' && !showInviteCode && (
              <div>
                <label className="block text-sm font-semibold mb-2">{t.bandName}</label>
                <input
                  type="text"
                  value={bandName}
                  onChange={(e) => setBandName(e.target.value)}
                  placeholder={t.bandNamePlaceholder}
                  className="w-full bg-transparent border border-surface-border-light px-4 py-3 text-white placeholder-text-subtle focus:outline-none focus:border-brand rounded-pill"
                  
                />
                <p className="text-xs text-text-subtle mt-1">{t.bandNameHelp}</p>
              </div>
            )}

            {/* INVITE CODE - Hidden behind link */}
            {authMode === 'signup' && (
              <>
                {showInviteCode ? (
                  <div>
                    <label className="block text-sm font-semibold mb-2">{t.inviteCode}</label>
                    <input
                      type="text"
                      value={inviteCode}
                      onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                      placeholder={t.inviteCodePlaceholder}
                      className="w-full bg-transparent border border-surface-border-light px-4 py-3 text-white placeholder-text-subtle focus:outline-none focus:border-brand uppercase rounded-pill"
                      
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setShowInviteCode(false);
                        setInviteCode('');
                        console.log('🎯 Invite code hidden');
                      }}
                      className="text-xs text-text-subtle hover:text-white mt-1 underline"
                    >
                      {t.hideInvite}
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <p className="text-xs text-text-subtle mb-1">{t.or}</p>
                    <button
                      type="button"
                      onClick={() => {
                        setShowInviteCode(true);
                        setBandName('');
                        console.log('🎯 Invite code shown');
                      }}
                      className="text-sm text-brand hover:text-brand-magenta font-semibold underline"
                    >
                      {t.joinExisting}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* REASSURANCE - Before submit button */}
            {authMode === 'signup' && (
              <div className="border border-surface-border-light rounded-card-btn p-3 text-center">
                <p className="text-xs text-text-muted">{t.reassurance}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={authLoading}
              className="w-full bg-brand hover:bg-brand-hover disabled:opacity-disabled disabled:cursor-not-allowed px-6 h-btn font-semibold transition rounded-pill"
              
            >
              {authLoading 
                ? (authMode === 'login' ? t.loggingIn : t.creatingAccount)
                : (authMode === 'login' ? t.login : t.createAccount)
              }
            </button>
          </form>
        </div>
      </div>

      {/* Password Reset Modal */}
      {showResetPassword && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="card-3d rounded-card p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-2">{t.resetPassword}</h2>
            
            {resetSuccess ? (
              <>
                <div className="bg-green-900/50 rounded-card p-4 mb-4">
                  <p className="text-green-100 text-sm">{t.resetSuccess}</p>
                </div>
                <button
                  onClick={() => {
                    setShowResetPassword(false);
                    setResetSuccess(false);
                    console.log('🎯 Back to login after reset');
                  }}
                  className="w-full bg-brand hover:bg-brand-hover px-6 h-btn font-semibold transition rounded-pill"
                  
                >
                  {t.backToLogin}
                </button>
              </>
            ) : (
              <>
                <p className="text-text-subtle text-sm mb-4">{t.resetInstructions}</p>
                
                {authError && (
                  <div className="bg-red-900/50 rounded-card p-3 mb-4">
                    <p className="text-red-100 text-sm">{authError}</p>
                  </div>
                )}
                
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">{t.email}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.emailPlaceholder}
                      className="w-full bg-transparent border border-surface-border-light px-4 py-3 text-white placeholder-text-subtle focus:outline-none focus:border-brand rounded-pill"
                      
                      autoFocus
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowResetPassword(false);
                        setAuthError('');
                        console.log('🎯 Cancelled password reset');
                      }}
                      className="flex-1 bg-transparent border border-surface-border-light hover:border-surface-border px-6 h-btn font-semibold transition rounded-pill"
                      
                    >
                      {t.backToLogin}
                    </button>
                    <button
                      type="submit"
                      disabled={authLoading}
                      className="flex-1 bg-brand hover:bg-brand-hover disabled:opacity-disabled disabled:cursor-not-allowed px-6 h-btn font-semibold transition rounded-pill"
                      
                    >
                      {authLoading ? t.sendingResetLink : t.sendResetLink}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
